import React, { useState } from "react";
import { motion } from "framer-motion";
import { FaLinkedin, FaEnvelope, FaWhatsapp, FaPaperPlane, FaCheck, FaExclamationTriangle } from "react-icons/fa";
import emailjs from "emailjs-com";

// ============================================================
// EMAILJS SETUP GUIDE:
// 1. Go to https://www.emailjs.com/ and create a free account
// 2. Add an email service (Gmail, Outlook, etc.) → copy the Service ID
// 3. Create an email template with variables: {{from_name}}, {{from_email}}, {{message}}
//    → copy the Template ID
// 4. Go to Account → API Keys → copy the Public Key
// 5. Replace the values below:
// ============================================================
const EMAILJS_SERVICE_ID = "service_npfpcyd";
const EMAILJS_TEMPLATE_ID = "template_tugc99k";
const EMAILJS_PUBLIC_KEY = "_qwpedG88fOL9_e-6";

const socialLinks = [
  {
    icon: <FaLinkedin />,
    href: "https://www.linkedin.com/in/petrus-ardi-tama/",
    label: "LinkedIn",
    handle: "/petrus-ardi-tama",
  },
  {
    icon: <FaWhatsapp />,
    href: "https://wa.me/6281585021394",
    label: "WhatsApp",
    handle: "+62 815-8502-1394",
  },
  {
    icon: <FaEnvelope />,
    href: "mailto:petrus.arditama@gmail.com",
    label: "Email",
    handle: "petrus.arditama@gmail.com",
  },
];

const Footer = () => {
  const [formData, setFormData] = useState({ from_name: "", from_email: "", message: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | success | error

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setStatus("sending");

    emailjs
      .send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, formData, EMAILJS_PUBLIC_KEY)
      .then(() => {
        setStatus("success");
        setFormData({ from_name: "", from_email: "", message: "" });
        setTimeout(() => setStatus("idle"), 4000);
      })
      .catch(() => {
        setStatus("error");
        setTimeout(() => setStatus("idle"), 4000);
      });
  };

  return (
    <footer id="contact" className="relative overflow-hidden">
      <div className="absolute inset-0 bg-dark-950" />
      <div className="absolute inset-0 grid-overlay" />

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <span className="font-mono text-xs text-gray-400 block mb-3">
            {"// "}section.contact
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4">
            Get In <span className="text-glow">Touch</span>
          </h2>
          <div className="section-divider mb-10 sm:mb-12 max-w-xs" />
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
          {/* Left side - Contact form */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <p className="text-gray-300 text-sm mb-6 leading-relaxed">
              Have a project in mind or want to collaborate? Drop me a message
              and I'll get back to you as soon as possible.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="font-mono text-[11px] text-gray-400 block mb-1.5">
                  name
                </label>
                <input
                  type="text"
                  name="from_name"
                  value={formData.from_name}
                  onChange={handleChange}
                  required
                  placeholder="John Doe"
                  className="w-full bg-dark-800 border border-sky-400/10 focus:border-sky-400/40 text-gray-200 text-sm px-4 py-3 outline-none transition-colors duration-300 placeholder:text-gray-600 font-mono"
                />
              </div>
              <div>
                <label className="font-mono text-[11px] text-gray-400 block mb-1.5">
                  email
                </label>
                <input
                  type="email"
                  name="from_email"
                  value={formData.from_email}
                  onChange={handleChange}
                  required
                  placeholder="john@example.com"
                  className="w-full bg-dark-800 border border-sky-400/10 focus:border-sky-400/40 text-gray-200 text-sm px-4 py-3 outline-none transition-colors duration-300 placeholder:text-gray-600 font-mono"
                />
              </div>
              <div>
                <label className="font-mono text-[11px] text-gray-400 block mb-1.5">
                  message
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  placeholder="Tell me about your project..."
                  className="w-full bg-dark-800 border border-sky-400/10 focus:border-sky-400/40 text-gray-200 text-sm px-4 py-3 outline-none transition-colors duration-300 placeholder:text-gray-600 font-mono resize-none"
                />
              </div>

              <button
                type="submit"
                disabled={status === "sending"}
                className={`btn-tech-filled w-full sm:w-auto justify-center ${
                  status === "sending" ? "opacity-60 cursor-wait" : ""
                }`}
              >
                {status === "sending" ? (
                  <>sending...</>
                ) : status === "success" ? (
                  <>
                    <FaCheck className="text-xs" />
                    message_sent!
                  </>
                ) : status === "error" ? (
                  <>
                    <FaExclamationTriangle className="text-xs" />
                    failed_retry
                  </>
                ) : (
                  <>
                    <FaPaperPlane className="text-xs" />
                    send_message
                  </>
                )}
              </button>

              {status === "success" && (
                <p className="font-mono text-xs text-emerald-400 mt-2">
                  {"// "}Thanks! I'll get back to you soon.
                </p>
              )}
              {status === "error" && (
                <p className="font-mono text-xs text-red-400 mt-2">
                  {"// "}Something went wrong. Try emailing me directly.
                </p>
              )}
            </form>
          </motion.div>

          {/* Right side - Social links */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <p className="text-gray-300 text-sm mb-6 leading-relaxed">
              Or reach out directly through any of these channels. I'm always
              open to new opportunities and interesting projects.
            </p>

            <div className="space-y-3 sm:space-y-4">
              {socialLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="glass-card p-4 sm:p-5 group flex items-center gap-4"
                >
                  <span className="text-lg text-gray-400 group-hover:text-sky-400 transition-colors duration-300">
                    {link.icon}
                  </span>
                  <div className="min-w-0">
                    <span className="font-mono text-xs text-gray-200 group-hover:text-sky-300 transition-colors duration-300 block">
                      {link.label}
                    </span>
                    <span className="font-mono text-[11px] text-gray-500 group-hover:text-gray-300 transition-colors duration-300 block truncate">
                      {link.handle}
                    </span>
                  </div>
                  <div className="ml-auto w-0 h-px bg-sky-400/50 group-hover:w-8 transition-all duration-500" />
                </a>
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="relative z-10 border-t border-sky-400/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <span className="font-mono text-[11px] text-gray-500">
            <span className="text-sky-400/50">{"<"}</span>
            built_by
            <span className="text-sky-400/50">{"/>"}</span>
            {" "}Petrus Arditama
          </span>
          <span className="font-mono text-[11px] text-gray-500">
            &copy; {new Date().getFullYear()} All rights reserved.
          </span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
